import { Alert, Button, Grid, Icon } from '@mui/material'
import React, { Dispatch } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { ChargeException } from '../../../../api/models/ChargeException.type'
import { ChargeRate, Taxi } from '../../../../api/vehicle/taxisAPI.types'
import { useSupplierState } from '../../../../Pages/Supplier/SupplierContext'
import styles from '../manageSupplierStyle'
import ChargeOverrides from './ChargeOverrides'
import DayRateRow from './DayRateRow'
import RoutePricingGridTitles from './RoutePricingGridTitles'

interface Props {
  taxi: Taxi | undefined
  waitingTime: string
  chargeRates: Array<ChargeRate>
  isEdit: boolean
  additionalStop: boolean
  waitAndReturn: boolean
  setChargeRates: Dispatch<React.SetStateAction<Array<ChargeRate>>>
}

export const RoutePricing = (props: Props) => {
  const {
    taxi,
    waitingTime,
    chargeRates,
    setChargeRates,
    isEdit,
    additionalStop,
    waitAndReturn,
  } = props
  const { chargeExceptions } = useSupplierState()
  const { control } = useFormContext()
  const watchedRates = useWatch({ control, name: 'chargeRates' }) as
    | Array<{ fromMile?: number | string; toMile?: number | string }>
    | undefined

  const displayOrder = React.useMemo(() => {
    const indexed = chargeRates.map((_, idx) => ({
      idx,
      fromMile: Number(watchedRates?.[idx]?.fromMile) || 0,
    }))
    indexed.sort((a, b) => a.fromMile - b.fromMile)
    return indexed.map((r) => r.idx)
  }, [chargeRates, watchedRates])

  const { mileageGaps, mileageOverlaps } = React.useMemo(() => {
    const rates = (watchedRates || [])
      .map((r, idx) => ({
        idx,
        fromMile: Number(r?.fromMile) || 0,
        toMile: Number(r?.toMile) || 0,
      }))
      .filter((r) => r.toMile > 0)
      .sort((a, b) => a.fromMile - b.fromMile)
    const gaps: Array<{ after: number; before: number }> = []
    const overlaps: Array<{ from: number; to: number }> = []
    for (let i = 0; i < rates.length - 1; i++) {
      if (rates[i].toMile < rates[i + 1].fromMile) {
        gaps.push({ after: rates[i].toMile, before: rates[i + 1].fromMile })
      } else if (rates[i].toMile > rates[i + 1].fromMile) {
        overlaps.push({
          from: rates[i + 1].fromMile,
          to: Math.min(rates[i].toMile, rates[i + 1].toMile),
        })
      }
    }
    return { mileageGaps: gaps, mileageOverlaps: overlaps }
  }, [watchedRates])

  const handleAdd = () => {
    let exceptions = chargeExceptions.map((e: ChargeException) => {
      if (chargeRates.length > 0) {
        const found: any = chargeRates[0].chargeExceptions.find(
          (ce) => ce?.chargeExceptionId === e.id,
        )
        return {
          chargeExceptionId: e.id,
          chargeTypeName: found?.chargeTypeName,
          percentage: found?.percentage,
          premium: found?.premium,
        }
      }
      return {
        chargeExceptionId: e.id,
        chargeTypeName: undefined,
        percentage: undefined,
        premium: undefined,
      }
    })

    const rate = {
      id: chargeRates.length + 1,
      taxiId: undefined,
      chargeExceptions: [...exceptions],
    }
    setChargeRates((prev: any) => [...prev, rate])
  }

  return (
    <>
      <Grid container item spacing={4} direction="row">
        {isEdit && (
          <Grid item>
            <Button
              variant="contained"
              endIcon={<Icon>add</Icon>}
              onClick={handleAdd}
              sx={styles.fieldSpacing}
            >
              Add mileage range
            </Button>
          </Grid>
        )}
      </Grid>
      {mileageGaps.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Gap in mileage coverage between rates:{' '}
          {mileageGaps.map((g) => `${g.after}–${g.before} miles`).join(', ')}.
          Please check this is correct
        </Alert>
      )}
      {mileageOverlaps.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Overlap in mileage coverage between rates:{' '}
          {mileageOverlaps.map((o) => `${o.from}–${o.to} miles`).join(', ')}.
          Please check this is correct
        </Alert>
      )}
      <RoutePricingGridTitles
        waitingTime={waitingTime}
        chargeRates={chargeRates}
        setChargeRates={setChargeRates}
        isEdit={isEdit}
        additionalStop={additionalStop}
        waitAndReturn={waitAndReturn}
        displayOrder={displayOrder}
      />
      <DayRateRow
        taxi={taxi}
        waitingTime={waitingTime}
        chargeRates={chargeRates}
        additionalStop={additionalStop}
        waitAndReturn={waitAndReturn}
        setChargeRates={setChargeRates}
        isEdit={isEdit}
        displayOrder={displayOrder}
      />
      <ChargeOverrides
        waitingTime={waitingTime}
        chargeRates={chargeRates}
        additionalStop={additionalStop}
        waitAndReturn={waitAndReturn}
        setChargeRates={setChargeRates}
        isEdit={isEdit}
        displayOrder={displayOrder}
      />
    </>
  )
}
export default RoutePricing
